import React, { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { LIBERTY_IMAGES } from '../assets/asset'
import { defaultTopTokenList } from '../constants/defaultTopTokens'
import { tokenList } from '../constants/tokens'
import { filterTokenList, TokenSelectorRows, toTokenSelection } from './tokenSelectorListUtils'

/** Above header (100), wallet adapter (1050), and in-app z-[9999] layers */
const TOKEN_SELECTOR_LAYER_Z = 2147483646
const PULSE_TOP_TOKEN_SYMBOLS = ['USDC', 'PLS', 'WETH', 'PLSX', 'HEX', 'INC', 'PCOCK', 'pDAI', 'eHEX', 'PRVX', 'ZERO']
const PRIVATE_EXCHANGE_PAY_PRIORITY = [
    '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48', // USDC Ethereum
    '0x8ac76a51cc950d9822d68b83fe1ad97b32cd580d', // USDC BSC
    '0xaf88d065e77c8cc2239327c5edb3a432268e5831', // USDC Arbitrum
    '0xc02aaA39b223FE8D0A0E5C4F27eAD9083C756Cc2'.toLowerCase(), // WETH Ethereum
    '0x2170ed0880ac9a755fd29b2688956bd959f933f8', // WETH BSC
]
const PRIVATE_EXCHANGE_RECEIVE_SYMBOL_PRIORITY = ['USDC', 'PLS', 'WETH', 'DAI', 'PLSX', 'HEX', 'INC']

const TokenSelector = ({ full, open = true, onClose, onSelect, listMode = 'combined' }) => {
    const [query, setQuery] = useState('')
    const [activeTopSymbol, setActiveTopSymbol] = useState(defaultTopTokenList[0]?.symbol || 'USDC')
    const isPulseOnly = listMode === 'pulse'
    const isDefaultOnly = listMode === 'default'
    const isShieldTopOnly = listMode === 'shield-top'
    const isRailgunOnly = listMode === 'railgun'
    const isUnshieldPayOnly = listMode === 'unshield-pay'
    const isUnshieldReceiveOnly = listMode === 'unshield-receive'
    const isPrivateExchangePayOnly = listMode === 'private-exchange-pay'
    const isPrivateExchangeReceiveOnly = listMode === 'private-exchange-receive'
    const allFilteredTokens = useMemo(() => filterTokenList(tokenList, query), [query])
    const isFlatListMode =
        isShieldTopOnly ||
        isRailgunOnly ||
        isUnshieldPayOnly ||
        isUnshieldReceiveOnly ||
        isPrivateExchangePayOnly ||
        isPrivateExchangeReceiveOnly
    const flatListRailgunBranding = isRailgunOnly || isUnshieldPayOnly || isPrivateExchangePayOnly
    const showSearchInput = !(isUnshieldPayOnly || isPrivateExchangePayOnly)
    const showUnshieldSwitch = isUnshieldPayOnly || isUnshieldReceiveOnly
    const showPrivateExchangeCtas = isPrivateExchangePayOnly || isPrivateExchangeReceiveOnly
    const pulseTokens = useMemo(
        () => tokenList.filter((token) => Number(token.chainId) === 369),
        []
    )

    const topTokenSymbols = useMemo(() => {
        const bySymbol = new Map()
        for (const token of defaultTopTokenList) {
            const symbol = token?.symbol
            if (!symbol || bySymbol.has(symbol)) continue
            bySymbol.set(symbol, token)
        }
        return [...bySymbol.keys()]
    }, [])

    const filteredDefaultTopTokens = useMemo(
        () => filterTokenList(defaultTopTokenList, query),
        [query]
    )
    const filteredDefaultRows = useMemo(() => {
        if (query.trim()) return filteredDefaultTopTokens
        return filteredDefaultTopTokens.filter((token) => token.symbol === activeTopSymbol)
    }, [activeTopSymbol, filteredDefaultTopTokens, query])
    const filteredTopSymbols = useMemo(
        () => topTokenSymbols.filter((symbol) => symbol.toLowerCase().includes(query.trim().toLowerCase())),
        [query, topTokenSymbols]
    )
    const pulseTopTokens = useMemo(() => {
        const bySymbol = new Map()
        for (const token of pulseTokens) {
            const symbol = token?.symbol
            if (!symbol || bySymbol.has(symbol)) continue
            bySymbol.set(symbol, token)
        }
        return PULSE_TOP_TOKEN_SYMBOLS.map((symbol) => bySymbol.get(symbol)).filter(Boolean)
    }, [pulseTokens])
    const filteredPulseTopTokens = useMemo(
        () => filterTokenList(pulseTopTokens, query),
        [pulseTopTokens, query]
    )
    const filteredPulseTokens = useMemo(
        () => filterTokenList(pulseTokens, query),
        [pulseTokens, query]
    )
    const orderedPrivateExchangePayTokens = useMemo(() => {
        const rank = new Map(PRIVATE_EXCHANGE_PAY_PRIORITY.map((addr, idx) => [addr.toLowerCase(), idx]))
        return [...tokenList]
            .filter((token) => Number(token.chainId) !== 369)
            .sort((a, b) => {
                const rankA = rank.has(a.address.toLowerCase()) ? rank.get(a.address.toLowerCase()) : Number.MAX_SAFE_INTEGER
                const rankB = rank.has(b.address.toLowerCase()) ? rank.get(b.address.toLowerCase()) : Number.MAX_SAFE_INTEGER
                if (rankA !== rankB) return rankA - rankB
                if (a.symbol !== b.symbol) return a.symbol.localeCompare(b.symbol)
                return a.chainId - b.chainId
            })
    }, [])
    const orderedPrivateExchangeReceiveTokens = useMemo(() => {
        const rank = new Map(PRIVATE_EXCHANGE_RECEIVE_SYMBOL_PRIORITY.map((symbol, idx) => [symbol, idx]))
        return [...pulseTokens].sort((a, b) => {
            const rankA = rank.has(a.symbol) ? rank.get(a.symbol) : Number.MAX_SAFE_INTEGER
            const rankB = rank.has(b.symbol) ? rank.get(b.symbol) : Number.MAX_SAFE_INTEGER
            if (rankA !== rankB) return rankA - rankB
            return a.symbol.localeCompare(b.symbol)
        })
    }, [pulseTokens])
    const privateExchangePayTokens = useMemo(
        () => orderedPrivateExchangePayTokens,
        [orderedPrivateExchangePayTokens]
    )
    const privateExchangeReceiveTokens = useMemo(
        () => orderedPrivateExchangeReceiveTokens,
        [orderedPrivateExchangeReceiveTokens]
    )
    const filteredPrivateExchangePayTokens = useMemo(
        () => filterTokenList(privateExchangePayTokens, query),
        [privateExchangePayTokens, query]
    )
    const filteredPrivateExchangeReceiveTokens = useMemo(
        () => filterTokenList(privateExchangeReceiveTokens, query),
        [privateExchangeReceiveTokens, query]
    )
    const flatListTokens = useMemo(() => {
        if (isPrivateExchangePayOnly) return filteredPrivateExchangePayTokens
        if (isPrivateExchangeReceiveOnly) return filteredPrivateExchangeReceiveTokens
        return allFilteredTokens
    }, [
        allFilteredTokens,
        filteredPrivateExchangePayTokens,
        filteredPrivateExchangeReceiveTokens,
        isPrivateExchangePayOnly,
        isPrivateExchangeReceiveOnly,
    ])

    useEffect(() => {
        if (!open) return
        setQuery('')
        if (isDefaultOnly) {
            setActiveTopSymbol(defaultTopTokenList[0]?.symbol || 'USDC')
        }
    }, [open, isDefaultOnly])

    const handleSelectRow = (sel) => {
        onSelect?.(sel)
        onClose?.()
    }

    const renderTopTokenChips = () => (
        <div className="flex flex-wrap gap-2">
            {isPulseOnly
                ? filteredPulseTopTokens.map((token) => {
                      const key = `${token.chainId}-${token.address}`
                      return (
                          <button
                              key={key}
                              type="button"
                              onClick={() => handleSelectRow(toTokenSelection(token))}
                              className="flex items-center gap-1 px-2 py-1 rounded-full transition-colors duration-150 bg-[#23232A] cursor-pointer hover:bg-[#2a2a33]"
                          >
                              <img
                                  alt={token.symbol}
                                  loading="lazy"
                                  width={20}
                                  height={20}
                                  decoding="async"
                                  data-nimg={1}
                                  className="rounded-full bg-[#41424B]"
                                  src={token.logoURI}
                                  style={{ color: 'transparent' }}
                              />
                              <span className="text-white text-xs font-medium mr-1">{token.symbol}</span>
                          </button>
                      )
                  })
                : filteredTopSymbols.map((symbol) => {
                      const isActive = symbol === activeTopSymbol
                      return (
                          <div
                              key={symbol}
                              className="rounded-full"
                              style={{
                                  padding: '1px',
                                  background: isActive
                                      ? 'linear-gradient(280.79deg, rgb(255, 101, 1) -2.22%, rgb(208, 59, 173) 52.36%, rgb(127, 0, 255) 102.39%)'
                                      : 'linear-gradient(rgba(255, 255, 255, 0.03) 0%, rgba(255, 255, 255, 0.1) 100%)',
                              }}
                          >
                              <button
                                  key={symbol}
                                  type="button"
                                  onClick={() => setActiveTopSymbol(symbol)}
                                  className={`px-4 py-1.5 rounded-full font-semibold text-sm transition-all duration-150 ${isActive ? 'text-white' : 'bg-[#23232A] text-[#9091A0]'}`}
                                  style={{
                                      outline: 'none',
                                      width: '100%',
                                      backgroundColor: isActive ? 'rgb(24, 24, 27)' : undefined,
                                      backgroundImage: isActive
                                          ? 'linear-gradient(280.79deg, rgba(255, 101, 1, 0.15) -2.22%, rgba(208, 59, 173, 0.15) 52.36%, rgba(127, 0, 255, 0.15) 102.39%)'
                                          : undefined,
                                  }}
                              >
                                  {symbol}
                              </button>
                          </div>
                      )
                  })}
        </div>
    )

    const renderSectionContent = (layout, infoIconSrc) => (
        <div className="mb-6">
            {isFlatListMode && (
                <div className="space-y-3">
                    <TokenSelectorRows
                        tokens={flatListTokens}
                        layout="default"
                        infoIconSrc={infoIconSrc}
                        railgunBranding={flatListRailgunBranding}
                        onSelectRow={handleSelectRow}
                    />
                    {flatListTokens.length === 0 && (
                        <div className="px-2 py-6 text-center text-sm text-[#9091A0]">No tokens found</div>
                    )}
                </div>
            )}
            {!isFlatListMode && (
            <>
            {(isPulseOnly ? filteredPulseTopTokens.length > 0 : filteredTopSymbols.length > 0) && (
                <>
                    {isPulseOnly && (
                        <div className="flex items-center gap-2 mb-3">
                            <span className="text-white font-medium text-sm">🔥 Top tokens</span>
                        </div>
                    )}
                    {renderTopTokenChips()}
                    {(isPulseOnly || (!isPulseOnly && !isDefaultOnly)) && (
                        <div className="border-t border-[#2D2D34] my-4" />
                    )}
                </>
            )}

            {(isDefaultOnly || (!isPulseOnly && !isDefaultOnly)) && filteredDefaultRows.length > 0 && (
                <>
                    {!isDefaultOnly && (
                        <div className="flex items-center gap-2 mb-3">
                            <span className="text-white font-medium text-sm">🌐 Default token list</span>
                        </div>
                    )}
                    <div className="space-y-3">
                        <TokenSelectorRows
                            tokens={filteredDefaultRows}
                            layout={layout}
                            infoIconSrc={infoIconSrc}
                            onSelectRow={handleSelectRow}
                        />
                    </div>
                    {!isDefaultOnly && <div className="border-t border-[#2D2D34] my-4" />}
                </>
            )}

            {(isPulseOnly || (!isPulseOnly && !isDefaultOnly)) && filteredPulseTokens.length > 0 && (
                <>
                    {isPulseOnly ? (
                        <div className="flex items-center gap-2 mb-3">
                            <span className="text-white font-medium text-sm">🔗 All tokens</span>
                        </div>
                    ) : (
                        <div className="flex items-center gap-2 mb-3">
                            <span className="text-white font-medium text-sm">🔗 Pulse tokens</span>
                        </div>
                    )}
                    <div className="space-y-3">
                        <TokenSelectorRows
                            tokens={filteredPulseTokens}
                            layout={layout}
                            infoIconSrc={infoIconSrc}
                            onSelectRow={handleSelectRow}
                        />
                    </div>
                </>
            )}

            {((isPulseOnly && filteredPulseTokens.length === 0 && filteredPulseTopTokens.length === 0) ||
                (isDefaultOnly && filteredDefaultRows.length === 0 && filteredTopSymbols.length === 0) ||
                (!isPulseOnly &&
                    !isDefaultOnly &&
                    filteredTopSymbols.length === 0 &&
                    filteredDefaultRows.length === 0 &&
                    filteredPulseTokens.length === 0)) && (
                <div className="px-2 py-6 text-center text-sm text-[#9091A0]">No tokens found</div>
            )}
            </>
            )}
        </div>
    )

    if (!open) {
        return null
    }
    const layer = (
        <div
            style={{
                position: 'fixed',
                inset: 0,
                zIndex: TOKEN_SELECTOR_LAYER_Z,
                pointerEvents: 'auto',
            }}
        >
            {!full ? (
                <>
                    <div id="modal-root">
                        <div
                            className="fixed inset-0 z-51 overflow-y-auto transition-all duration-300 ease-in-out modal-fullscreen-ios opacity-100"
                            style={{ inset: 0, width: "100vw" }}
                        >
                            <div className="flex min-h-screen items-center justify-center p-4 text-center modal-fullscreen-ios">
                                <div
                                    className="fixed bg-black/70 backdrop-blur-sm transition-opacity duration-300 ease-in-out modal-fullscreen-ios opacity-100"
                                    style={{ position: "fixed", inset: 0, width: "100vw", zIndex: -1 }}
                                />
                                <div
                                    className="p-4 pt-0 relative w-full max-w-[500px] transform rounded-2xl bg-[#0F0F0F] border border-[#41424B] text-left align-middle shadow-xl transition-all duration-300 ease-out 
      translate-y-0 scale-100 opacity-100"
                                >
                                    <div className="flex items-center justify-between py-4">
                                        <h3 className="text-lg font-medium text-white">Select a token</h3>
                                        <button
                                            type="button"
                                            onClick={() => onClose?.()}
                                            className="flex items-center justify-center w-8 h-8 rounded-full bg-black/20 backdrop-blur-sm text-neutral-400 hover:text-white hover:bg-black/40 transition-all duration-200 border border-neutral-600/30">
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="h-4 w-4"
                                                viewBox="0 0 20 20"
                                                fill="currentColor"
                                            >
                                                <path
                                                    fillRule="evenodd"
                                                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                                    clipRule="evenodd"
                                                />
                                            </svg>
                                        </button>
                                    </div>
                                    <div className="max-h-[80vh] overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                                        <div className="h-[440px] overflow-hidden flex flex-col modal-content">
                                            {showSearchInput && (
                                            <div className="sticky top-0 z-20 pt-2 pb-0 mb-4">
                                                <div className="relative">
                                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                        <img
                                                            alt="Search"
                                                            loading="lazy"
                                                            width={20}
                                                            height={20}
                                                            decoding="async"
                                                            data-nimg={1}
                                                            src="https://x.libertyswap.finance/base/ic-search.svg"
                                                            style={{ color: "transparent" }}
                                                        />
                                                    </div>
                                                    <input
                                                        placeholder="Search tokens"
                                                        className="w-full pl-10 pr-10 py-2 bg-[#2D2D34] border border-[#41424B] rounded-xl text-white placeholder-[#9091A0] focus:outline-none focus:ring-[#393941]"
                                                        type="text"
                                                        value={query}
                                                        onChange={(e) => setQuery(e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                            )}
                                            <div className="flex-1 overflow-y-auto scrollbar-hide">
                                                {renderSectionContent("default", "https://x.libertyswap.finance/base/ic-fill-information.svg")}
                                            </div>
                                            {showUnshieldSwitch && (
                                                <div className="sticky top-0 z-20 mx-[24px]! md:mx-0! mb-4">
                                                    <div className="w-full mt-3 bg-[#18181B] border-[#242429] border rounded-xl overflow-hidden">
                                                        <button type="button" className="w-full px-4 py-3 flex items-center justify-between transition-colors">
                                                            <span className="text-white text-sm font-normal">
                                                                Railgun to Pulsechain?<span className="text-[#F5A425] text-sm font-medium"> Switch to Private mode</span>
                                                            </span>
                                                            <img alt="Chevron Right" src={LIBERTY_IMAGES.IC_ARROW_RIGHT} className="w-5 h-5 opacity-70" />
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                            {showPrivateExchangeCtas && (
                                                <div className="sticky top-0 z-20 mx-[24px]! md:mx-0! mb-4">
                                                    <div className="w-full mt-3 bg-[#18181B] border-[#242429] border rounded-xl overflow-hidden">
                                                        <button type="button" className="w-full px-4 py-3 flex items-center justify-between transition-colors">
                                                            <span className="text-white text-sm font-normal">
                                                                More pairs?<span className="text-[#F5A425] text-sm font-medium"> Switch to Public mode</span>
                                                            </span>
                                                            <img alt="Chevron Right" src={LIBERTY_IMAGES.IC_ARROW_RIGHT} className="w-5 h-5 opacity-70" />
                                                        </button>
                                                        <div className="border-t border-dashed border-[#2D2D34]" />
                                                        <button type="button" className="w-full px-4 py-3 flex items-center justify-between transition-colors">
                                                            <span className="text-white text-sm font-normal">
                                                                Shield your assets?<span className="text-[#F5A425] text-sm font-medium"> Go to Shield</span>
                                                            </span>
                                                            <img alt="Chevron Right" src={LIBERTY_IMAGES.IC_ARROW_RIGHT} className="w-5 h-5 opacity-70" />
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            ) : (
                <>
                    <div id="modal-root">
                        <div
                            className="fixed inset-0 z-50 overflow-y-auto transition-all duration-300 ease-in-out modal-fullscreen-ios opacity-100"
                            style={{ inset: 0, width: "100vw" }}
                        >
                            <div className="flex min-h-screen items-center justify-center p-4 text-center modal-fullscreen-ios">
                                <div
                                    className="fixed bg-black/70 backdrop-blur-sm transition-opacity duration-300 ease-in-out modal-fullscreen-ios opacity-100"
                                    style={{ position: "fixed", inset: 0, width: "100vw", zIndex: -1 }}
                                />
                                <div
                                    className="p-4 pt-0 relative w-full max-w-[500px] transform rounded-2xl bg-[#18181B] border border-[#41424B] text-left align-middle shadow-xl transition-all duration-300 ease-out 
      translate-y-0 scale-100 opacity-100"
                                >
                                    <div className="flex items-center justify-between py-4">
                                        <h3 className="text-lg font-medium text-white">Select a token</h3>
                                        <button
                                            type="button"
                                            onClick={() => onClose?.()}
                                            className="flex items-center justify-center w-8 h-8 rounded-full bg-black/20 backdrop-blur-sm text-neutral-400 hover:text-white hover:bg-black/40 transition-all duration-200 border border-neutral-600/30">
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="h-4 w-4"
                                                viewBox="0 0 20 20"
                                                fill="currentColor"
                                            >
                                                <path
                                                    fillRule="evenodd"
                                                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                                    clipRule="evenodd"
                                                />
                                            </svg>
                                        </button>
                                    </div>
                                    <div className="max-h-[80vh] overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                                        <div className="h-[440px] overflow-hidden flex flex-col modal-content">
                                            {showSearchInput && (
                                            <div className="sticky top-0 z-20 bg-[#18181B] pt-2 pb-0 mb-4">
                                                <div className="relative">
                                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                        <img
                                                            alt="Search"
                                                            loading="lazy"
                                                            width={20}
                                                            height={20}
                                                            decoding="async"
                                                            data-nimg={1}
                                                            src="https://x.libertyswap.finance/base/ic-search.svg"
                                                            style={{ color: "transparent" }}
                                                        />
                                                    </div>
                                                    <input
                                                        placeholder="Search tokens"
                                                        className="w-full pl-10 pr-10 py-2 bg-[#2D2D34] border border-[#41424B] rounded-xl text-white placeholder-[#9091A0] focus:outline-none focus:ring-[#393941]"
                                                        type="text"
                                                        value={query}
                                                        onChange={(e) => setQuery(e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                            )}
                                            <div className="flex-1 overflow-y-auto scrollbar-hide">
                                                {renderSectionContent("compact", LIBERTY_IMAGES.IC_FILL_INFORMATION)}
                                            </div>
                                            {showUnshieldSwitch && (
                                                <div className="sticky top-0 z-20 mx-[24px]! md:mx-0! mb-4">
                                                    <div className="w-full mt-3 bg-[#18181B] border-[#242429] border rounded-xl overflow-hidden">
                                                        <button type="button" className="w-full px-4 py-3 flex items-center justify-between transition-colors">
                                                            <span className="text-white text-sm font-normal">
                                                                Railgun to Pulsechain?<span className="text-[#F5A425] text-sm font-medium"> Switch to Private mode</span>
                                                            </span>
                                                            <img alt="Chevron Right" src={LIBERTY_IMAGES.IC_ARROW_RIGHT} className="w-5 h-5 opacity-70" />
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                            {showPrivateExchangeCtas && (
                                                <div className="sticky top-0 z-20 mx-[24px]! md:mx-0! mb-4">
                                                    <div className="w-full mt-3 bg-[#18181B] border-[#242429] border rounded-xl overflow-hidden">
                                                        <button type="button" className="w-full px-4 py-3 flex items-center justify-between transition-colors">
                                                            <span className="text-white text-sm font-normal">
                                                                More pairs?<span className="text-[#F5A425] text-sm font-medium"> Switch to Public mode</span>
                                                            </span>
                                                            <img alt="Chevron Right" src={LIBERTY_IMAGES.IC_ARROW_RIGHT} className="w-5 h-5 opacity-70" />
                                                        </button>
                                                        <div className="border-t border-dashed border-[#2D2D34]" />
                                                        <button type="button" className="w-full px-4 py-3 flex items-center justify-between transition-colors">
                                                            <span className="text-white text-sm font-normal">
                                                                Shield your assets?<span className="text-[#F5A425] text-sm font-medium"> Go to Shield</span>
                                                            </span>
                                                            <img alt="Chevron Right" src={LIBERTY_IMAGES.IC_ARROW_RIGHT} className="w-5 h-5 opacity-70" />
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    )
    return createPortal(layer, document.body)
}

export default TokenSelector